import { useAuth } from '@clerk/clerk-react';
import { setSentryUser } from '@nearst/sentry-config';
import * as Sentry from '@sentry/react';
import { useShop } from '@services/ShopProvider';
import { getAuthSession } from '@services/auth';
import shopApi from '@services/shop';
import mixpanel from 'mixpanel-browser';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { useHubspot } from '../../utils/hubspot';

const usePageViews = () => {
	let location = useLocation();

	useEffect(() => {
		const page = location.pathname;

		// Strips the shops uuid out of the url so we can track pages correctly
		const title = page.replace(/\/[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/, '');
		ReactGA.send({ hitType: 'pageview', page, title: `my_shop_${title}` });
		mixpanel.track('Page view', { distinct_id: mixpanel.get_distinct_id(), title, page });
	}, [location]);
};

const useAnalytics = () => {
	const { actor, isSignedIn } = useAuth();

	useEffect(() => {
		if (!actor && isSignedIn) {
			shopApi.updateLastLogin().catch(console.error);
		}
	}, [actor, isSignedIn]);
};

const useMixpanelIdentity = () => {
	const { shop: currentShop, data: shops } = useShop();
	const { actor } = useAuth();

	useEffect(() => {
		(async () => {
			const { email } = await getAuthSession();
			// Missing email = Clerk hasn't resolved a primary address (phone-first sign-up, or
			// a user who later adds one). Bail without calling opt_out_tracking, which writes
			// a persistent flag we can't undo when the email eventually appears.
			if (!email) return;
			if (email.includes('@near.st') || currentShop?.featureFlags?.includes('mixpanel-exclude') || !!actor) {
				mixpanel.opt_out_tracking();
				return;
			}

			mixpanel.identify(email);
			mixpanel.people.set({
				$email: email,
				USER_ID: email
			});

			const shop = currentShop.id ? currentShop : shops?.[0];

			if (shop) {
				mixpanel.people.set({
					'Organisation ID': shop.organisationId,
					'Organisation Name': shop.organisation,
					'Key Account Managed': !!shop.keyAccountManaged,
					'Subscription Plan': shop.plan,
					'Last Login': new Date().toISOString()
				});
			}
		})();
	}, [currentShop, shops, actor]);
};

const useSentry = () => {
	const { actor, isSignedIn, userId, orgSlug, orgId } = useAuth();
	useEffect(() => {
		if (actor || !isSignedIn || !userId) {
			setSentryUser(Sentry, { userId: null });
			return;
		}
		setSentryUser(Sentry, { userId, organisation: orgSlug, clerkOrganisationId: orgId });
	}, [actor, isSignedIn, userId, orgSlug, orgId]);
};

const AnalyticsWrapper = ({ children }) => {
	useHubspot();
	useMixpanelIdentity();
	usePageViews();
	useAnalytics();
	useSentry();
	return children;
};

export default AnalyticsWrapper;
